<div class="min-h-screen bg-sketch-pattern text-black font-sans selection:bg-[#D4AF37] selection:text-white">

  <!-- Section 1: Hero -->
  <!-- Ensure strict full screen height and full width coverage for laptop -->
  <header
    class="min-h-[100dvh] h-screen min-h-screen w-full relative overflow-hidden flex flex-col justify-center items-center">
    <!-- Video Background - Forced Strict Fullscreen -->
    <div class="absolute inset-0 z-0 bg-black w-full h-full">
      <!-- High-Quality Image Fallback & Placeholder for Slow Connections -->
      <img src="/content/images/interior_fitouts_bg.webp" fetchpriority="high" loading="eager"
        alt="Vettro Traders Interior Fitouts"
        class="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 animate-float-fallback"
        [class.opacity-40]="currentHeroVideoIndex() === 0" [class.opacity-0]="currentHeroVideoIndex() !== 0">

      <!-- Crossfading Videos -->
      @for (video of heroVideos(); track video; let i = $index) {
      <video #heroVideoEl [src]="video" [autoplay]="i === 0" muted loop playsinline
        title="Vettro Traders Interior Fitouts Hero Video"
        aria-label="Hero showcase of Vettro Traders interior projects" poster="/content/images/interior_fitouts_bg.webp"
        [preload]="i === currentHeroVideoIndex() ? 'auto' : 'none'"
        class="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out"
        [class.opacity-100]="i === currentHeroVideoIndex()" [class.opacity-0]="i !== currentHeroVideoIndex()"
        [class.z-10]="i === currentHeroVideoIndex()" [class.z-0]="i !== currentHeroVideoIndex()">
        <track kind="captions" src="/content/videos/empty-captions.vtt" srclang="en" label="English" default>
      </video>
      }

      <!-- Dark Gradient Overlay (Applied OVER the 100% opaque videos to achieve the desired darkness) -->
      <div class="absolute inset-0 bg-gradient-to-br from-black/80 via-black/50 to-black/80 z-20 mix-blend-multiply">
      </div>
      <div class="absolute inset-0 bg-black/20 z-20"></div>
    </div>

    <!-- Hero Content (Text & Button) - Revealed on Scroll -->
    <div
      class="z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto mt-16 sm:mt-20 transition-all duration-1000 ease-[cubic-bezier(0.25,0.8,0.25,1)]"
      [class.opacity-0]="!hasScrolled()" [class.translate-y-20]="!hasScrolled()"
      [class.pointer-events-none]="!hasScrolled()" [class.opacity-100]="hasScrolled()"
      [class.translate-y-0]="hasScrolled()">

      <h1
        class="text-3xl sm:text-4xl md:text-7xl lg:text-8xl font-bold leading-tight mb-4 sm:mb-6 tracking-tight text-white text-shadow-lg">
        The Integrated <br />
        <span class="italic font-serif text-[#D4AF37]">Interior Ecosystem</span>
      </h1>
      <p
        class="text-gray-200 max-w-2xl mx-auto text-base sm:text-xl md:text-2xl font-light mb-6 sm:mb-10 text-shadow-lg px-1">
        From Raw Materials to Final Execution.
      </p>
      <a routerLink="/contact"
        class="px-6 sm:px-10 py-3 sm:py-4 bg-[#D4AF37] text-black hover:bg-white hover:text-black transition-all duration-300 rounded-full font-bold uppercase tracking-widest text-xs sm:text-sm shadow-xl hover:shadow-2xl transform hover:-translate-y-1 inline-block cursor-pointer pointer-events-auto">
        Get Quote
      </a>
    </div>

    <!-- Cinematic Scroll Indicator (Hidden after scroll) -->
    <div
      class="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center transition-all duration-700 ease-in-out"
      [class.opacity-100]="!hasScrolled()" [class.opacity-0]="hasScrolled()"
      [class.pointer-events-none]="hasScrolled()">
      <span class="text-white text-xs tracking-[0.3em] uppercase mb-4 opacity-70 font-light">Explore</span>
      <div class="w-[1px] h-16 bg-white/20 relative overflow-hidden">
        <div class="w-full h-1/2 bg-[#D4AF37] absolute top-[-50%] animate-scroll-line"></div>
      </div>
    </div>
  </header>

  <!-- Section 2: Premium Sub-Brand Showcase with Stunning Title -->
  <div
    class="w-full bg-white relative z-20 shadow-[0_-20px_40px_rgba(0,0,0,0.1)] transition-opacity duration-1000 ease-[cubic-bezier(0.25,0.8,0.25,1)]"
    [class.opacity-0]="!hasScrolled()" [class.pointer-events-none]="!hasScrolled()" [class.opacity-100]="hasScrolled()">
    <div class="max-w-7xl mx-auto px-3 sm:px-6 relative">
      <!-- Premium Title Section -->
      <div class="text-center mb-8 sm:mb-12 pt-10 sm:pt-16">
        <span
          class="block text-[#8A6D1C] tracking-[0.2em] sm:tracking-[0.3em] font-medium mb-2 sm:mb-3 uppercase text-[10px] sm:text-xs">Integrated
          Ecosystem</span>
        <h2
          class="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-gray-900 via-[#D4AF37] to-gray-900 bg-clip-text text-transparent px-2">
          Our <span class="italic font-serif">Brands</span>
        </h2>
        <p class="text-gray-500 text-xs sm:text-sm md:text-base max-w-2xl mx-auto px-2">Click to explore each branch
          of our manufacturing excellence.</p>
      </div>

      <!-- Brand Cards - Now Clickable -->
      <div class="flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-6 pb-6 sm:pb-8">
        @for (brand of subBrands(); track brand.name) {
        <a [href]="brand.mapUrl" target="_blank"
          class="glossy-card-premium rounded-xl p-3 sm:p-4 flex items-center justify-center w-full min-[400px]:w-[48%] sm:w-[48%] md:w-[28%] lg:w-[18%] aspect-[5/2] group cursor-pointer transform hover:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-[#D4AF37]/20 relative overflow-hidden">
          <!-- Gradient Overlay on Hover -->
          <div
            class="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 to-[#D4AF37]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          </div>
          <div class="w-28 h-28 relative">
            <img [ngSrc]="brand.logo" [alt]="brand.name" width="200" height="80"
              class="w-full h-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500 transform group-hover:scale-110 relative z-10">
          </div>
        </a>
        }
      </div>


    </div>
  </div>

  <main class="space-y-10 sm:space-y-16">

    <!-- Section 3: Vision (Split Layout) with Fixed Background & Glassmorphism -->
    <section class="about-section-fixed-bg w-full relative py-12 sm:py-20 md:py-32 overflow-hidden">
      <!-- Fixed Background Image -->
      <div class="absolute inset-0 fixed-bg-image"></div>

      <!-- Gradient Overlay for Better Readability -->
      <div class="absolute inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/70"></div>

      <!-- Texture Overlay for Premium Feel -->
      <div class="absolute inset-0 opacity-5"
        style="background-image: url('data:image/svg+xml,%3Csvg width=&quot;100&quot; height=&quot;100&quot; xmlns=&quot;http://www.w3.org/2000/svg&quot;%3E%3Cpattern id=&quot;a&quot; patternUnits=&quot;userSpaceOnUse&quot; width=&quot;100&quot; height=&quot;100&quot;%3E%3Cpath d=&quot;M0 0h100v100H0z&quot; fill=&quot;none&quot;/%3E%3Cpath d=&quot;M0 50h100M50 0v100&quot; stroke=&quot;%23fff&quot; stroke-width=&quot;0.5&quot; opacity=&quot;0.3&quot;/%3E%3C/pattern%3E%3Crect width=&quot;100%25&quot; height=&quot;100%25&quot; fill=&quot;url(%23a)&quot;/%3E%3C/svg%3E');">
      </div>

      <div class="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center">

          <!-- Text Content with Premium Glassmorphism -->
          <div
            class="glassmorphism-card p-5 sm:p-8 md:p-12 rounded-2xl sm:rounded-3xl relative overflow-hidden order-2 lg:order-1 transform hover:scale-[1.02] transition-all duration-500">
            <!-- Gold Accent Border -->
            <div class="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-[#D4AF37] via-[#FFD700] to-[#D4AF37]">
            </div>

            <!-- Animated Border Glow -->
            <div class="absolute inset-0 rounded-3xl border border-[#D4AF37]/20 animate-pulse-slow"></div>

            <!-- Content -->
            <div class="relative z-10">
              <span
                class="block text-[#D4AF37] tracking-[0.3em] font-medium mb-4 uppercase text-xs md:text-sm animate-fade-in">About
                Us</span>

              <h2 class="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-white leading-tight">
                The Vision Behind
                <span class="text-gradient-gold-animated italic font-serif block mt-2">Vettro</span>.
              </h2>

              <p
                class="text-gray-200 text-base md:text-lg mb-8 font-light italic border-l-4 border-[#D4AF37]/50 pl-6 py-2 bg-white/5 rounded-r-xl backdrop-blur-sm">
                "Built over two decades of experience in interior materials and execution."
              </p>

              <div class="space-y-5 text-gray-300 mb-10 text-sm md:text-base leading-relaxed">
                <p class="timeline-item">
                  Our journey began in <strong class="text-[#D4AF37] font-bold">2002</strong>, with the foundation of
                  Venice Furnishings and
                  Venice Metals, focusing on interior decor materials and aluminium systems.
                </p>
                <p class="timeline-item">
                  In <strong class="text-[#D4AF37] font-bold">2011</strong>, we expanded into glass & plywood solutions
                  with the launch of
                  Real Glass & Paints, serving residential and commercial projects.
                </p>
                <p class="timeline-item">
                  By <strong class="text-[#D4AF37] font-bold">2019</strong>, we brought everything together under Vettro
                  Traders &
                  Integrated Services, along with V-Decor, to offer a complete, factory-backed interior ecosystem — from
                  materials to
                  execution.
                </p>
                <p class="timeline-item font-medium text-white">
                  <strong class="text-[#D4AF37]">Today</strong>, Vettro operates as a unified platform built on
                  experience,
                  capability, and responsibility.
                </p>
              </div>

              <!-- Features List with Enhanced Styling -->
              <ul class="space-y-4 mb-10">
                <li class="flex items-center space-x-3 group">
                  <span class="material-icons text-[#D4AF37] group-hover:scale-125 transition-transform">verified</span>
                  <span class="font-medium text-white group-hover:text-[#D4AF37] transition-colors">In-house
                    Manufacturing</span>
                </li>
                <li class="flex items-center space-x-3 group">
                  <span class="material-icons text-[#D4AF37] group-hover:scale-125 transition-transform">verified</span>
                  <span class="font-medium text-white group-hover:text-[#D4AF37] transition-colors">Integrated
                    Brands</span>
                </li>
                <li class="flex items-center space-x-3 group">
                  <span class="material-icons text-[#D4AF37] group-hover:scale-125 transition-transform">verified</span>
                  <span class="font-medium text-white group-hover:text-[#D4AF37] transition-colors">End-to-End
                    Execution</span>
                </li>
              </ul>

              <a routerLink="/ecosystem"
                class="premium-cta-button px-8 py-4 border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all uppercase tracking-widest text-xs font-bold rounded-xl relative overflow-hidden group inline-block">
                <span class="relative z-10">Explore The Ecosystem</span>
                <div
                  class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000">
                </div>
              </a>
            </div>
          </div>

          <!-- Image Content with Premium Frame -->
          <div class="relative group order-1 lg:order-2">
            <!-- Glow Effect -->
            <div
              class="absolute -inset-4 bg-gradient-to-br from-[#D4AF37]/30 via-[#FFD700]/20 to-[#D4AF37]/30 rounded-2xl blur-2xl group-hover:blur-3xl transition-all duration-500 opacity-75">
            </div>

            <!-- Image Container -->
            <div
              class="relative w-full h-[280px] sm:h-[360px] md:h-[600px] bg-black/30 rounded-xl sm:rounded-2xl shadow-2xl border-2 sm:border-4 border-white/10 overflow-hidden backdrop-blur-sm">
              <img src="/content/images/Vettro%20Building%20Image.webp" alt="Vettro Traders Head Office" loading="lazy"
                class="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 opacity-90 group-hover:opacity-100">

              <!-- Image Overlay Gradient -->
              <div
                class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              </div>
            </div>

            <!-- Decorative Corner Accent -->
            <div class="absolute -top-2 -right-2 w-20 h-20 border-t-4 border-r-4 border-[#D4AF37] rounded-tr-2xl"></div>
            <div class="absolute -bottom-2 -left-2 w-20 h-20 border-b-4 border-l-4 border-[#D4AF37] rounded-bl-2xl">
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- Section 4: Video Showcase (Refined Height & Flow) -->
    <section class="w-full bg-gradient-to-r from-black via-gray-900 to-black py-6 sm:py-8 relative overflow-hidden">
      <div class="max-w-7xl mx-auto px-3 sm:px-4 text-center mb-4 sm:mb-6 relative z-10">
        <h2 class="text-xl sm:text-2xl md:text-5xl font-bold text-white mb-2">Vettro Video <span
            class="text-[#D4AF37] italic font-serif">Showcase</span></h2>
      </div>

      <div
        class="max-w-3xl mx-auto relative p-2 sm:p-3 architectural-frame rounded-xl bg-black/50 backdrop-blur-sm border border-gray-800 px-2 sm:px-4">
        @if (currentShowcaseVideo()) {
        <div class="relative aspect-video bg-black rounded-lg overflow-hidden group shadow-2xl">
          <video #showcaseVideo [src]="currentShowcaseVideo()" loop [muted]="isMuted()" preload="none"
            title="Vettro Video Showcase" aria-label="Detailed video showcase of Vettro interior execution capabilities"
            poster="/content/images/Vettro%20Building%20Image.webp"
            class="w-full h-full object-cover transition-opacity duration-500">
            <track kind="captions" src="/content/videos/empty-captions.vtt" srclang="en" label="English" default>
          </video>

          <!-- Big Center Play Button (Only when paused) -->
          @if (!showcasePlaying()) {
          <button (click)="toggleShowcasePlay(showcaseVideo)"
            class="absolute inset-0 flex items-center justify-center cursor-pointer group/play z-40 transition-all duration-300">
            <div
              class="glass-play-button w-20 h-20 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300 bg-black/30 backdrop-blur-sm border border-white/30">
              <span
                class="material-icons text-white text-6xl ml-2 group-hover/play:text-[#D4AF37] transition-colors">play_arrow</span>
            </div>
          </button>
          }
        </div>

        <!-- Custom Bottom Control Bar (Below Video) -->
        <div class="w-full bg-black flex items-center justify-between p-4 rounded-b-lg mt-1">
          <!-- Left: Play/Pause -->
          <button (click)="toggleShowcasePlay(showcaseVideo)"
            class="text-white hover:text-[#D4AF37] transition-colors p-2">
            <span class="material-icons text-3xl">{{ showcasePlaying() ? 'pause' : 'play_arrow' }}</span>
          </button>

          <!-- Center: Optional Label -->
          <div class="text-white/50 text-xs uppercase tracking-widest hidden md:block">
            Vettro Video Showcase
          </div>

          <!-- Right: Controls & Navigation -->
          <div class="flex items-center space-x-2 md:space-x-4">
            <!-- Mute Toggle -->
            <button (click)="toggleMute(showcaseVideo)" class="text-white hover:text-[#D4AF37] transition-colors p-2">
              <span class="material-icons text-2xl">{{ isMuted() ? 'volume_off' : 'volume_up' }}</span>
            </button>

            <!-- Navigation Separator -->
            <div class="h-6 w-px bg-white/20"></div>

            <!-- Prev -->
            <button (click)="prevShowcase(); $event.stopPropagation()"
              class="text-white hover:text-[#D4AF37] transition-colors p-2">
              <span class="material-icons text-3xl">chevron_left</span>
            </button>

            <!-- Next -->
            <button (click)="nextShowcase(); $event.stopPropagation()"
              class="text-white hover:text-[#D4AF37] transition-colors p-2">
              <span class="material-icons text-3xl">chevron_right</span>
            </button>
          </div>
        </div>
        } @else {
        <div class="relative aspect-video bg-black rounded-lg overflow-hidden group shadow-2xl">
          <div class="w-full h-full flex items-center justify-center text-gray-600">No Video Available</div>
        </div>
        }
      </div>
    </section>

    <!-- Section 5: Marquee Gallery (Refined Flow) -->
    <section class="w-full bg-white py-10 sm:py-16 overflow-hidden">
      <div class="text-center mb-6 sm:mb-10 px-2">
        <h2 class="text-2xl sm:text-3xl md:text-5xl font-bold tracking-tight mb-2">Our Work <span
            class="text-[#8A6D1C]">Showcase</span>
        </h2>
        <p class="text-gray-500 text-sm sm:text-base">Excellence in every detail.</p>
      </div>

      <!-- Row 1: Left to Right with Drag Navigation -->
      <div class="relative w-full mb-6">
        <!-- Gallery Container -->
        <div #galleryRow1Container
          class="gallery-container relative w-full overflow-hidden flex hover:pause select-none"
          (mousedown)="onDragStart($event, 'row1')" (mousemove)="onDragMove($event, 'row1')"
          (mouseup)="onDragEnd('row1')" (mouseleave)="onDragEnd('row1')" (touchstart)="onTouchStartDrag($event, 'row1')"
          (touchmove)="onTouchMoveDrag($event, 'row1')" (touchend)="onTouchEndDrag('row1')">

          <!-- Moving Inner Container -->
          <div #row1Mover class="flex gap-4 min-w-full will-change-transform backface-hidden">
            @for (item of galleryRow1(); track item._id) {
            <div
              class="flex-shrink-0 w-80 h-60 rounded-xl overflow-hidden relative group cursor-pointer pointer-events-none">
              <img [ngSrc]="item.imageUrl" [alt]="item.altText" width="320" height="240"
                sizes="(max-width: 640px) 100vw, 33vw"
                class="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700">
            </div>
            }
            <!-- Duplicate for loop -->
            @for (item of galleryRow1(); track item._id) {
            <div
              class="flex-shrink-0 w-80 h-60 rounded-xl overflow-hidden relative group cursor-pointer pointer-events-none">
              <img [ngSrc]="item.imageUrl" [alt]="item.altText" width="320" height="240"
                sizes="(max-width: 640px) 100vw, 33vw"
                class="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700">
            </div>
            }
          </div>
        </div>
      </div>

      <!-- Row 2: Right to Left with Drag Navigation -->
      <div class="relative w-full">
        <!-- Gallery Container -->
        <div #galleryRow2Container
          class="gallery-container relative w-full overflow-hidden flex hover:pause select-none"
          (mousedown)="onDragStart($event, 'row2')" (mousemove)="onDragMove($event, 'row2')"
          (mouseup)="onDragEnd('row2')" (mouseleave)="onDragEnd('row2')" (touchstart)="onTouchStartDrag($event, 'row2')"
          (touchmove)="onTouchMoveDrag($event, 'row2')" (touchend)="onTouchEndDrag('row2')">

          <!-- Moving Inner Container -->
          <div #row2Mover class="flex gap-4 min-w-full will-change-transform backface-hidden">
            @for (item of galleryRow2(); track item._id) {
            <div
              class="flex-shrink-0 w-80 h-60 rounded-xl overflow-hidden relative group cursor-pointer pointer-events-none">
              <img [ngSrc]="item.imageUrl" [alt]="item.altText" width="320" height="240"
                sizes="(max-width: 640px) 100vw, 33vw"
                class="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700">
            </div>
            }
            <!-- Duplicate for loop -->
            @for (item of galleryRow2(); track item._id) {
            <div
              class="flex-shrink-0 w-80 h-60 rounded-xl overflow-hidden relative group cursor-pointer pointer-events-none">
              <img [ngSrc]="item.imageUrl" [alt]="item.altText" width="320" height="240"
                sizes="(max-width: 640px) 100vw, 33vw"
                class="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700">
            </div>
            }
          </div>
        </div>
      </div>
    </section>

    <!-- Section: Impact Statistics -->
    <section #statsSection class="w-full py-12 sm:py-20 md:py-24 mesh-gradient-bg relative overflow-hidden">
      <!-- Darker overlay (30%) to ensure text visibility while keeping flow effect -->
      <div class="absolute inset-0 bg-black/40 backdrop-blur-[1px]"></div>
      <div class="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 relative z-10">
        <!-- Quote -->
        <div class="text-center max-w-4xl mx-auto mb-10 sm:mb-20 px-2">
          <span class="material-icons text-4xl sm:text-5xl text-[#D4AF37] mb-4 sm:mb-6 opacity-80">format_quote</span>
          <h2 class="text-xl sm:text-3xl md:text-5xl font-bold text-white leading-tight mb-4 sm:mb-6">
            Discover our story through numbers. <br />
            <span class="text-gradient-gold italic font-serif">Excellence is our only standard.</span>
          </h2>
          <p class="text-gray-200 text-base sm:text-lg md:text-xl font-light">From material supply to full execution.
          </p>
        </div>

        <!-- Stats Grid -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
          @for (stat of impactStats(); track stat.label) {
          <div
            class="bg-black/40 backdrop-blur-md border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 text-center hover:bg-black/60 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] group">
            <h3
              class="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-1 sm:mb-2 tracking-tighter group-hover:text-[#D4AF37] transition-colors">
              {{ stat.current }}{{ stat.suffix }}
            </h3>
            <p class="text-[#D4AF37] font-bold uppercase tracking-widest text-xs sm:text-sm mb-2 sm:mb-3">{{ stat.label
              }}</p>
            <p class="text-gray-400 text-xs sm:text-sm font-light border-t border-white/10 pt-2 sm:pt-3">{{
              stat.description }}</p>
          </div>
          }
        </div>
      </div>
    </section>

    <!-- Section 6: Trusted Partners (Marquee) -->
    <app-partner-slider></app-partner-slider>

    <!-- Section 7: Our Branches -->
    <app-experience-centers [branches]="branches()"></app-experience-centers>

  </main>

  <!-- Floating WhatsApp Button -->
  <a href="https://wa.me/919447772931?text=Hello%2C%20I%20visited%20vettrotraders.com%20and%20I%20am%20interested%20in%20your%20Interior%20Ecosystem%20products.%20Please%20guide%20me."
    target="_blank"
    class="fixed right-3 sm:right-6 bottom-24 sm:bottom-32 z-50 group transition-all duration-700 ease-[cubic-bezier(0.25,0.8,0.25,1)] hover:scale-110"
    [class.opacity-0]="!hasScrolled()" [class.pointer-events-none]="!hasScrolled()"
    [class.translate-y-20]="!hasScrolled()" [class.opacity-100]="hasScrolled()" [class.translate-y-0]="hasScrolled()">
    <div class="absolute inset-0 bg-[#25D366] rounded-full animate-ping opacity-75"></div>
    <div
      class="relative bg-[#25D366] p-3 rounded-full shadow-2xl flex items-center justify-center border-2 border-white/20">
      <!-- Inline WhatsApp SVG -->
      <svg class="w-8 h-8 fill-white" viewBox="0 0 24 24">
        <path
          d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.463 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
      </svg>
    </div>
    <span
      class="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-black text-white text-xs px-3 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
      Chat for Enquiry
    </span>
  </a>

  <!-- Q-TUFF Floating Banner (Fixed Bottom Right via CSS) -->
  @if(showQTuffBanner()) {
  <div
    class="q-tuff-banner flex items-center space-x-4 px-6 py-4 rounded-lg cursor-pointer transition-all duration-700 ease-[cubic-bezier(0.25,0.8,0.25,1)] transform hover:scale-105"
    [class.opacity-0]="!hasScrolled()" [class.pointer-events-none]="!hasScrolled()"
    [class.translate-y-20]="!hasScrolled()" [class.opacity-100]="hasScrolled()" [class.translate-y-0]="hasScrolled()"
    role="button">
    <div class="flex flex-col">
      <span class="text-[10px] text-[#D4AF37] uppercase tracking-[0.2em]">The Future of Safety Glass</span>
      <span class="text-xl shimmer-text">Q-TUFF <span class="text-xs text-gray-400 font-normal ml-2">Coming
          Soon</span></span>
    </div>
    <button (click)="closeQTuff(); $event.stopPropagation()" class="text-gray-500 hover:text-white"><span
        class="material-icons text-sm">close</span></button>
  </div>
  }

  <!-- Section: The Journey Continues (Next Step) -->
  <app-next-step></app-next-step>

  <!-- Section 8: SEO Footer -->
  <app-footer
    [description]="'Vettro Traders & Integrated Services is a leading interior solutions provider serving Alappuzha, Ernakulam, Kottayam and greater Kerala. Your partner in premium execution.'"></app-footer>
</div>